import clsx from 'clsx'
import PropTypes from 'prop-types'
import { Fragment, useRef } from 'react'

import { useFrame } from '../../../engine'
import { prog, pv, stagger } from '../../../motion'

const range = PropTypes.arrayOf(PropTypes.number)

/**
 * Wörter gleiten einzeln aus einer Maske nach oben ins Bild, optional im
 * Bereich `out` wieder hinaus.
 */
export function MaskText({ text, at, out, className }) {
  const words = text.split(' ')
  const step = (at[1] - at[0]) / Math.max(1, words.length)
  return (
    <span className={clsx('sc-mask', className)}>
      {words.map((word, i) => (
        // Das Leerzeichen steht außerhalb der Maske, sonst schluckt inline-block es
        <Fragment key={i}>
          <span className='sc-mask__w'>
            <span
              className='sc-mask__i'
              style={{
                ...pv('m', [at[0] + step * i, at[0] + step * (i + 3)]),
                ...(out ? pv('n', out) : null)
              }}
            >
              {word}
            </span>
          </span>{' '}
        </Fragment>
      ))}
    </span>
  )
}

MaskText.propTypes = {
  text: PropTypes.string.isRequired,
  at: range.isRequired,
  out: range,
  className: PropTypes.string
}

/**
 * Zahl, deren Ziffern wie bei einem Kilometerzähler hochrollen.
 * Nicht-Ziffern (Punkt, Prozent, Plus) stehen fest.
 */
export function Odometer({ value, at, className }) {
  const chars = [...String(value)]
  const digits = chars.filter((ch) => /\d/.test(ch)).length
  const len = at[1] - at[0]
  let k = 0
  return (
    <span className={clsx('sc-odo', className)} aria-label={value} role='img'>
      {chars.map((ch, i) => {
        if (!/\d/.test(ch)) {
          return (
            <span key={i} className='sc-odo__c' aria-hidden='true'>
              {ch === ' ' ? '\u00A0' : ch}
            </span>
          )
        }
        const index = k++
        const spins = 1 + ((digits - index) % 3)
        const total = Number(ch) + spins * 10
        return (
          <span key={i} className='sc-odo__d' aria-hidden='true'>
            <span
              className='sc-odo__col'
              style={{
                ...prog(stagger(at[0], len * 0.06, len * 0.7, index)),
                transform: `translateY(calc(var(--p) * ${-total} * var(--row)))`
              }}
            >
              {Array.from({ length: total + 1 }, (_, j) => (
                <span key={j}>{j % 10}</span>
              ))}
            </span>
          </span>
        )
      })}
    </span>
  )
}

Odometer.propTypes = {
  value: PropTypes.string.isRequired,
  at: range.isRequired,
  className: PropTypes.string
}

/** Link oder Button, der sich leicht zum Mauszeiger hinzieht. */
export function Magnetic({ as: Tag = 'a', className, children, ...rest }) {
  const ref = useRef(null)
  const move = (e) => {
    const el = ref.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const dx = (e.clientX - (r.left + r.width / 2)) * 0.28
    const dy = (e.clientY - (r.top + r.height / 2)) * 0.36
    el.style.transform = `translate(${dx}px, ${dy}px)`
  }
  const leave = () => {
    if (ref.current) ref.current.style.transform = ''
  }
  return (
    <Tag
      ref={ref}
      className={clsx('sc-magnetic', className)}
      onPointerMove={move}
      onPointerLeave={leave}
      {...rest}
    >
      {children}
    </Tag>
  )
}

Magnetic.propTypes = {
  as: PropTypes.elementType,
  className: PropTypes.string,
  children: PropTypes.node
}

/**
 * Video, das nur im Bereich `at` der Section läuft und sonst pausiert.
 * Startet jedes Mal von vorn, wenn es wieder ins Spiel kommt.
 */
export function ScrollVideo({ src, poster, at, className, label }) {
  const ref = useRef(null)
  const playing = useRef(false)
  useFrame((l) => {
    const video = ref.current
    if (!video) return
    const on = l >= at[0] && l <= at[1]
    if (on === playing.current) return
    playing.current = on
    if (on) {
      video.currentTime = 0
      video.play().catch(() => {})
    } else {
      video.pause()
    }
  })
  return (
    <video
      ref={ref}
      className={className}
      src={src}
      poster={poster}
      muted
      loop
      playsInline
      preload='metadata'
      aria-label={label}
    />
  )
}

ScrollVideo.propTypes = {
  src: PropTypes.string.isRequired,
  poster: PropTypes.string,
  at: range.isRequired,
  className: PropTypes.string,
  label: PropTypes.string
}
